import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Avatar } from "../avatars";
import useFriendRequests from "../../hooks/useFriendRequests";
import { logCustomEvent } from "../../services/analytics/analytics";
import { ANALYTICS_EVENTS } from "../../services/analytics/events";
import "./FriendRequests.css";

const FriendRequests = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    requests,
    error,
    isProcessingRequest,
    acceptRequest,
    declineRequest,
    hasRequests,
  } = useFriendRequests();

  if (!hasRequests) return null;

  const getDisplayName = (u) =>
    u.displayName || u.username || t("friends.anonymous_friend");

  return (
    <div className="friend-requests">
      <h2 className="friend-requests-title">{t("friends.friend_requests")}</h2>
      {error && <div className="friend-requests-error">{error}</div>}

      <div className="friend-requests-list">
        {requests.map((req) => (
          <div
            key={req.id}
            className="friend-request-card"
            onClick={(e) => {
              if (!e.target.closest("button")) navigate(`/profile/${req.id}`);
            }}
            role="button"
            tabIndex="0"
            onKeyPress={(e) =>
              (e.key === "Enter" || e.key === " ") &&
              navigate(`/profile/${req.id}`)
            }
          >
            <div className="friend-request-user">
              <div className="friend-request-avatar">
                <Avatar
                  name={getDisplayName(req)}
                  src={req.photoURL}
                  size="fluid"
                  alt={t("friends.profile_picture_alt", {
                    name: getDisplayName(req),
                  })}
                  textColor="var(--background-elevated)"
                />
              </div>
              <div className="friend-request-info">
                <h3>{getDisplayName(req)}</h3>
                {req.username && (
                  <p className="friend-request-username">@{req.username}</p>
                )}
              </div>
            </div>
            <div className="friend-request-actions">
              <button
                className="friend-request-button accept"
                onClick={(e) => {
                  e.stopPropagation();
                  acceptRequest(req.id);
                  logCustomEvent(ANALYTICS_EVENTS.FRIEND_REQUEST_ACCEPTED, {
                    user_id: req.id,
                  });
                }}
                disabled={isProcessingRequest(req.id)}
                aria-label={t("friends.accept_request_aria", {
                  name: getDisplayName(req),
                })}
              >
                {isProcessingRequest(req.id)
                  ? t("friends.processing")
                  : t("friends.accept")}
              </button>
              <button
                className="friend-request-button decline"
                onClick={(e) => {
                  e.stopPropagation();
                  declineRequest(req.id);
                }}
                disabled={isProcessingRequest(req.id)}
                aria-label={t("friends.decline_request_aria", {
                  name: getDisplayName(req),
                })}
              >
                {t("friends.decline")}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FriendRequests;
